// SPDX-License-Identifier: SEE LICENSE
//
// Sessions browser — landing page for the dashboard. Lists every device
// chain ever run, with live-streaming sessions surfacing to the top
// with an animated pulse. Filter tabs cut the grid by status. Server
// persists sessions to disk (tmp/sessions.json) so the list survives
// backend restarts; the file is robust enough for the on-prem demo
// deployment without needing a separate Postgres table for sessions
// (per-pillar verdicts already live in Prisma).
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Layout } from '../components/Layout';
import { useAuth } from '../auth';
import { useSessionsStream } from '../lib/useSessionsStream';
import type { SessionListItem, StepKind } from '../lib/types';

type FilterTab = 'all' | 'live' | 'pass' | 'concern' | 'fail' | 'partial' | 'abandoned';
type CardStatus = 'live' | 'pass' | 'concern' | 'fail' | 'partial' | 'abandoned';

const PILLAR_LABEL: Record<string, string> = {
  antenna: 'Antenna / RF',
  battery_soh: 'Battery State-of-Health',
  battery_forecast: 'Battery Forecast',
  touch: 'Touch Controller',
  oled: 'Display Burn-In',
  vibro: 'Structural Resonance',
  magneto: 'Magnetometer',
  selfemit: 'Self-Emission',
  ultrasonic: 'Acoustic',
  drop: 'Drop Forensics',
  acta: 'Component Authenticity',
  thermal: 'Thermal Envelope',
  wipe: 'Secure Wipe',
};

const STATUS_COLOR: Record<CardStatus, { fg: string; bg: string; border: string; label: string }> = {
  pass: { fg: '#34d399', bg: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.40)', label: 'PASS' },
  concern: { fg: '#f5bf45', bg: 'rgba(245,191,69,0.10)', border: 'rgba(245,191,69,0.40)', label: 'CONCERN' },
  fail: { fg: '#f06a5e', bg: 'rgba(240,106,94,0.10)', border: 'rgba(240,106,94,0.40)', label: 'FAIL' },
  live: { fg: '#15ddc9', bg: 'rgba(20,222,205,0.10)', border: 'rgba(20,222,205,0.40)', label: 'IN PROGRESS' },
  partial: { fg: '#9fb3b8', bg: 'rgba(159,179,184,0.08)', border: 'rgba(159,179,184,0.32)', label: 'PARTIAL' },
  abandoned: { fg: '#7f9aa0', bg: 'rgba(127,154,160,0.08)', border: 'rgba(127,154,160,0.30)', label: 'ABANDONED' },
};

// statusOf — STRICT: the only way a card animates as "IN PROGRESS" is
// if a pillar is genuinely capturing right now (`active > 0`).
// Finalized sessions are never live, even if their overall verdict is
// 'incomplete' (e.g. the operator finalized after skipping some pillars).
// Unfinalized sessions with no live capture are "abandoned" — they were
// started but never run to completion.
function statusOf(s: SessionListItem): CardStatus {
  if ((s.active ?? 0) > 0) return 'live';
  if (s.finalized_at) {
    if (s.overall === 'pass') return 'pass';
    if (s.overall === 'concern') return 'concern';
    if (s.overall === 'fail') return 'fail';
    return 'partial'; // finalized but pillars left undone
  }
  if (s.overall === 'pass' || s.overall === 'concern' || s.overall === 'fail') {
    return s.overall;
  }
  return 'abandoned';
}

function relTime(ts: number) {
  const ago = Math.max(0, Date.now() - ts);
  const m = Math.floor(ago / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fmtDuration(start: number, end: number | null) {
  const ms = Math.max(0, (end ?? Date.now()) - start);
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

export function Sessions() {
  const { sessions, connected } = useSessionsStream();
  const { state: auth, logout } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterTab>('all');
  const [query, setQuery] = useState('');

  const sorted = useMemo(() => [...sessions].sort((a, b) => b.started_at - a.started_at), [sessions]);

  const counts = useMemo(() => {
    const c = { all: sorted.length, live: 0, pass: 0, concern: 0, fail: 0, partial: 0, abandoned: 0 };
    for (const s of sorted) {
      const st = statusOf(s);
      c[st]++;
    }
    return c;
  }, [sorted]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((s) => {
      const st = statusOf(s);
      if (filter !== 'all' && st !== filter) return false;
      if (!q) return true;
      const haystack = `${s.id} ${s.device_model ?? ''} ${s.device_fingerprint ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [sorted, filter, query]);

  return (
    <Layout connected={connected} hideHeader>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 4 }}>
        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <img
              src="/retronics-logo.jpeg"
              alt="Retronics"
              style={{
                width: 42,
                height: 42,
                borderRadius: 10,
                objectFit: 'contain',
                border: '1px solid rgba(20,222,205,0.4)',
                background: 'rgba(20,222,205,0.07)',
                boxShadow: '0 0 22px -4px rgba(20,222,205,0.6)',
              }}
            />
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2.2, color: '#6f878e', fontWeight: 600 }}>
                LIVE DIAGNOSTIC ENGINE
              </div>
              <div style={{ fontSize: 26, fontWeight: 600, color: '#f4faf9', marginTop: 4, letterSpacing: '-0.5px' }}>
                Diagnostic Sessions
              </div>
              <div style={{ fontSize: 12, color: '#6f878e', marginTop: 4 }}>
                Every device chain that ever ran — past, present, and live · {sorted.length} session{sorted.length === 1 ? '' : 's'} on record
              </div>
            </div>
          </div>
          {auth.enforced !== false && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {auth.user && (
                <span style={{ fontSize: 11.5, color: '#6f878e', letterSpacing: 0.4 }}>
                  signed in as <span style={{ color: '#cfe0e0', fontWeight: 600 }}>{auth.user}</span>
                </span>
              )}
              <button
                onClick={() => logout()}
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: '#cfe0e0',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(120,160,160,0.22)',
                  borderRadius: 8,
                  padding: '7px 14px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* FLEET METRICS */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            border: '1px solid rgba(120,160,160,0.16)',
            borderRadius: 14,
            background: 'rgba(9,16,26,0.55)',
            padding: '18px 6px',
          }}
        >
          <FleetCell n={counts.all} label="TOTAL SESSIONS" color="#cfe0e0" />
          <FleetCell n={counts.live} label="IN PROGRESS" color="#15ddc9" pulse divider />
          <FleetCell n={counts.pass} label="PASSED" color="#34d399" divider />
          <FleetCell n={counts.concern} label="CONCERN" color="#f5bf45" divider />
          <FleetCell n={counts.fail} label="FAILED" color="#f06a5e" divider />
          <FleetCell n={counts.abandoned} label="ABANDONED" color="#7f9aa0" divider />
        </div>

        {/* FILTER TABS + SEARCH */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, border: '1px solid rgba(120,160,160,0.18)', borderRadius: 11, padding: 4, background: 'rgba(9,16,26,0.55)', flexWrap: 'wrap' }}>
            <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={counts.all} />
            <FilterPill active={filter === 'live'} onClick={() => setFilter('live')} label="Live" count={counts.live} color="#15ddc9" pulse={counts.live > 0} />
            <FilterPill active={filter === 'pass'} onClick={() => setFilter('pass')} label="Passed" count={counts.pass} color="#34d399" />
            <FilterPill active={filter === 'concern'} onClick={() => setFilter('concern')} label="Concern" count={counts.concern} color="#f5bf45" />
            <FilterPill active={filter === 'fail'} onClick={() => setFilter('fail')} label="Failed" count={counts.fail} color="#f06a5e" />
            <FilterPill active={filter === 'partial'} onClick={() => setFilter('partial')} label="Partial" count={counts.partial} color="#9fb3b8" />
            <FilterPill active={filter === 'abandoned'} onClick={() => setFilter('abandoned')} label="Abandoned" count={counts.abandoned} color="#7f9aa0" />
          </div>
          <div style={{ flex: 1, minWidth: 200, maxWidth: 460 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by session id, device model, fingerprint…"
              style={{
                width: '100%',
                fontSize: 12,
                color: '#cfe0e0',
                background: 'rgba(9,16,26,0.55)',
                border: '1px solid rgba(120,160,160,0.18)',
                borderRadius: 10,
                padding: '8px 12px',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
          {counts.live > 0 && (
            <button
              onClick={() => {
                const live = sorted.find((s) => statusOf(s) === 'live');
                if (live) navigate(`/sessions/${live.id}`);
              }}
              style={{
                fontSize: 11.5,
                color: '#15ddc9',
                background: 'rgba(20,222,205,0.08)',
                border: '1px solid rgba(20,222,205,0.4)',
                borderRadius: 9,
                padding: '8px 14px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              <span className="animate-livepulse" style={{ width: 7, height: 7, borderRadius: '50%', background: '#15ddc9', boxShadow: '0 0 6px #15ddc9' }} />
              Jump to live session
            </button>
          )}
        </div>

        {/* GRID */}
        {filtered.length === 0 ? (
          <EmptyState filter={filter} hasAny={sorted.length > 0} />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))',
              gap: 13,
            }}
          >
            {filtered.map((s) => (
              <SessionCard key={s.id} s={s} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function SessionCard({ s }: { s: SessionListItem }) {
  const st = statusOf(s);
  const palette = STATUS_COLOR[st];
  const total = s.passed + s.concerns + s.failed + s.skipped + s.pending;
  const done = total - s.pending;
  const progressPct = total > 0 ? (done / total) * 100 : 0;
  // True live only when a pillar is genuinely capturing right now. An
  // abandoned/partial session is NOT live and does not animate.
  const isLive = st === 'live';
  const isAbandoned = st === 'abandoned' || st === 'partial';
  const activeKind = s.activeKind ? PILLAR_LABEL[s.activeKind as string] ?? s.activeKind : null;

  return (
    <Link
      to={`/sessions/${s.id}`}
      style={{
        display: 'block',
        padding: '14px 16px',
        borderRadius: 13,
        border: `1px solid ${isLive ? palette.border : 'rgba(120,160,160,0.16)'}`,
        background: isLive
          ? 'linear-gradient(180deg, rgba(20,222,205,0.05), rgba(9,16,26,0.6))'
          : 'rgba(9,16,26,0.55)',
        textDecoration: 'none',
        color: 'inherit',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: isLive ? '0 0 28px rgba(20,222,205,0.08)' : 'none',
        transition: 'transform 200ms ease, border-color 200ms ease',
      }}
      className="session-card-hover"
    >
      {isLive && (
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: 'linear-gradient(90deg, transparent, #15ddc9, transparent)',
            animation: 'ldeSweep 2.4s linear infinite',
          }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#f4faf9', letterSpacing: '-0.2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {s.device_model ?? 'Unknown device'}
          </div>
          <div style={{ fontSize: 10.5, fontFamily: "'JetBrains Mono', monospace", color: '#6f878e', marginTop: 4 }}>
            {s.id.slice(0, 8)} · {relTime(s.started_at)}
          </div>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: 1.2,
            color: palette.fg,
            background: palette.bg,
            border: `1px solid ${palette.border}`,
            borderRadius: 7,
            padding: '4px 9px',
            whiteSpace: 'nowrap',
            flex: 'none',
          }}
        >
          {isLive && (
            <span
              className="animate-livepulse"
              style={{ width: 6, height: 6, borderRadius: '50%', background: palette.fg, boxShadow: `0 0 5px ${palette.fg}` }}
            />
          )}
          {palette.label}
        </span>
      </div>

      {/* progress bar */}
      <div style={{ marginTop: 12, position: 'relative', height: 6, borderRadius: 3, background: 'rgba(20,40,48,0.6)', overflow: 'hidden', border: '1px solid rgba(45,95,105,0.18)' }}>
        <div
          style={{
            width: `${progressPct}%`,
            height: '100%',
            background: isLive ? 'linear-gradient(90deg, #1f9f98, #15ddc9)' : palette.fg,
            transition: 'width 700ms cubic-bezier(0.22, 1, 0.36, 1)',
            boxShadow: isLive ? '0 0 10px rgba(20,222,205,0.6)' : 'none',
            position: 'relative',
          }}
        >
          {isLive && (
            <span
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: 16,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45))',
                animation: 'ldeSweep 1.2s linear infinite',
              }}
            />
          )}
        </div>
      </div>

      {/* status counters */}
      <div style={{ marginTop: 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 11 }}>
          <CounterPill n={s.passed} color="#34d399" label="pass" />
          <CounterPill n={s.concerns} color="#f5bf45" label="concern" />
          <CounterPill n={s.failed} color="#f06a5e" label="fail" />
          {s.skipped > 0 && <CounterPill n={s.skipped} color="#6f878e" label="skip" />}
        </div>
        <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: '#6f878e' }}>
          {done}/{total}
        </span>
      </div>

      {/* live activity / completion footer */}
      <div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px dashed rgba(120,160,160,0.16)', fontSize: 11, color: '#9fb3b8', display: 'flex', alignItems: 'center', gap: 7 }}>
        {isLive ? (
          <>
            <span className="animate-livepulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#15ddc9', boxShadow: '0 0 5px #15ddc9' }} />
            <span>
              {activeKind ? <>Capturing <span style={{ color: '#cfe0e0', fontWeight: 600 }}>{activeKind}</span></> : 'Awaiting first pillar…'}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#6f878e' }}>
              {fmtDuration(s.started_at, null)}
            </span>
          </>
        ) : isAbandoned ? (
          <>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            <span>Not finished · {done}/{total} pillars</span>
            <span style={{ flex: 1 }} />
          </>
        ) : (
          <>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <span>Completed</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#6f878e' }}>
              {fmtDuration(s.started_at, s.finalized_at ?? Date.now())}
            </span>
          </>
        )}
      </div>
    </Link>
  );
}

function CounterPill({ n, color, label }: { n: number; color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: n > 0 ? color : '#3a4f59' }}>{n}</span>
      <span style={{ color: '#5d7178', fontSize: 10 }}>{label}</span>
    </span>
  );
}

function FleetCell({
  n,
  label,
  color,
  pulse,
  divider,
}: {
  n: number;
  label: string;
  color: string;
  pulse?: boolean;
  divider?: boolean;
}) {
  return (
    <div style={{ textAlign: 'center', borderLeft: divider ? '1px solid rgba(120,160,160,0.14)' : 'none', padding: '0 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
        {pulse && n > 0 && (
          <span
            className="animate-livepulse"
            style={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}` }}
          />
        )}
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 26, fontWeight: 700, color, letterSpacing: '-0.5px' }}>
          {n}
        </span>
      </div>
      <div style={{ fontSize: 10, letterSpacing: 1.6, color: '#6f878e', fontWeight: 600, marginTop: 5 }}>
        {label}
      </div>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  label,
  count,
  color,
  pulse,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  color?: string;
  pulse?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 11.5,
        fontWeight: active ? 600 : 500,
        color: active ? '#04090f' : color ?? '#cfe0e0',
        background: active ? color ?? '#cfe0e0' : 'transparent',
        border: 'none',
        borderRadius: 8,
        padding: '7px 12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        letterSpacing: 0.3,
        transition: 'all 200ms ease',
      }}
    >
      {pulse && !active && (
        <span className="animate-livepulse" style={{ width: 5, height: 5, borderRadius: '50%', background: color ?? '#15ddc9' }} />
      )}
      {label}
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10.5,
          background: active ? 'rgba(0,0,0,0.18)' : 'rgba(120,160,160,0.12)',
          color: active ? '#04090f' : '#9fb3b8',
          borderRadius: 5,
          padding: '1px 7px',
          fontWeight: 600,
        }}
      >
        {count}
      </span>
    </button>
  );
}

function EmptyState({ filter, hasAny }: { filter: FilterTab; hasAny: boolean }) {
  const msg = !hasAny
    ? 'No sessions on record yet. Start the Device Health Check on the connected handset — the first chain will appear here in real time.'
    : `No ${filter === 'live' ? 'live' : filter} sessions match.`;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        border: '1px dashed rgba(120,160,160,0.22)',
        borderRadius: 14,
        background: 'rgba(9,16,26,0.45)',
      }}
    >
      <div
        className="animate-livepulse"
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          border: '2px solid rgba(20,222,205,0.4)',
          boxShadow: '0 0 22px -2px rgba(20,222,205,0.4) inset, 0 0 14px rgba(20,222,205,0.25)',
        }}
      />
      <div style={{ marginTop: 16, fontSize: 13, fontWeight: 600, color: '#cfe0e0' }}>
        {!hasAny ? 'Waiting for a device…' : 'No matches'}
      </div>
      <div style={{ marginTop: 6, fontSize: 11.5, color: '#6f878e', maxWidth: 460, textAlign: 'center' }}>
        {msg}
      </div>
    </div>
  );
}

// Suppress unused-import lint for StepKind (it's referenced via PILLAR_LABEL keys).
void ({} as { _?: StepKind });
